import type { ExternalAudioDevice } from './callAudioRoute';

/**
 * Что сейчас на самом деле стоит в нативном аудиоменеджере звонка.
 *
 * <p><b>Зачем это нужно.</b> `InCallManager.start()` на обеих платформах
 * выходит первой же строкой, если менеджер уже запущен:
 * iOS — {@code if (_audioSessionInitialized) return;}, Android —
 * {@code if (!audioManagerActivated) {…}}. Значит, повторный вызов с другим
 * `media` <b>ничего не меняет</b>: на iOS остаётся прежний `_media`
 * (а от него зависит, поставит ли `updateAudioRoute` режим `VideoChat`), на
 * Android — прежний `defaultAudioDevice` (а его и выбирает «автоматический»
 * маршрут). Отсюда весь класс дефектов «видеозвонок играет в разговорный
 * динамик» и «аудиозвонок орёт в громкий»: режим звонка на телефоне менялся
 * (входящий приходит как `audio`, а `callMode` приезжает в `invite`
 * следом), а нативная сторона оставалась настроенной на прошлый.
 *
 * <p>Нативного геттера «с каким media ты запущен» у модуля нет, поэтому
 * ведём учёт здесь. Инвариант: {@link inCallManagerMedia} возвращает не-null
 * ровно тогда, когда менеджер запущен, — поэтому <b>каждый</b>
 * `InCallManager.stop()` в приложении обязан отметиться через
 * {@link noteInCallManagerStopped}, в том числе за пределами звонка
 * (проигрывание голосовых сообщений, запись — экран чата).
 */
let startedMedia: 'audio' | 'video' | null = null;

/**
 * Последняя известная внешняя гарнитура. Нужна, чтобы отличить «маршрут
 * выбирает платформа, потому что воткнули наушники» от «маршрут выбираем мы,
 * потому что платформенная лестница до нас не доходит» — см.
 * {@link speakerFlagForCall}.
 */
let externalDevice: ExternalAudioDevice | null = null;

/**
 * Отметить запуск. Повторный вызов при уже запущенном менеджере намеренно
 * ничего не меняет: нативный `start()` в этом случае выходит первой же
 * строкой, режим остаётся прежним — и запомнить новый значило бы соврать про
 * то, как настроена сессия.
 */
export function noteInCallManagerStarted(media: 'audio' | 'video'): void {
  if (startedMedia !== null) return;
  startedMedia = media;
}

export function noteInCallManagerStopped(): void {
  startedMedia = null;
}

/** С каким `media` запущен нативный менеджер, или null, если он не запущен. */
export function inCallManagerMedia(): 'audio' | 'video' | null {
  return startedMedia;
}

export function noteExternalAudioDevice(device: ExternalAudioDevice | null): void {
  externalDevice = device;
}

export function currentExternalAudioDevice(): ExternalAudioDevice | null {
  return externalDevice;
}

export function resetCallAudioNativeStateForTests(): void {
  startedMedia = null;
  externalDevice = null;
}
